"use client"

import { useState, useEffect, useCallback, useRef } from "react"
import { useParams, useSearchParams, useNavigate, Link } from "react-router-dom"
import { useAuth } from "@/hooks/useAuth"
import { getJobLogs, getJobStatus } from "@/services/hpcJobsService"
import { HpcJob, JobStatus } from "@/types/hpc-jobs"
import {
  formatJobDuration,
  formatJobTimestamp,
  getStatusVariant,
  transformJobData,
} from "@/lib/hpc-jobs-utils"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { CopyButton } from "@/components/ui/copy-button.tsx"
import { DetailField } from "@/components/hpc-jobs/DetailField"
import { ArrowLeft, Loader2, RefreshCw } from "lucide-react"
import LoadingSpinner from "@/components/shared/LoadingSpinner"

/** Number of log entries to fetch on initial load. */
const INITIAL_LIMIT = 100

/** Interval in milliseconds between tail polls. */
const TAIL_INTERVAL_MS = 5000

/** A single log entry from the API. */
interface LogEntry {
  timestamp: number
  ingestionTime: number
  message: string
}

/**
 * Formats a log timestamp (epoch ms) into a human-readable string.
 *
 * @param timestamp - Epoch milliseconds.
 * @returns Formatted date string in Australia/Perth timezone.
 */
function formatLogTimestamp(timestamp: number): string {
  return new Intl.DateTimeFormat("en-AU", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    timeZone: "Australia/Perth",
    hour12: false,
  }).format(new Date(timestamp))
}

/**
 * Resolves which array job child to open from the childIndex query param.
 *
 * Anything that is not a whole number inside the array's range (including a
 * missing param, or any value at all for a non-array job) falls back to the
 * first child, which is the only index guaranteed to exist.
 *
 * @param childIndexParam - Raw childIndex query param value, or null if absent.
 * @param arraySize - Number of child tasks in the array job, or 0 if not an array job.
 * @returns The child index to select initially.
 */
export function resolveInitialChildIndex(
  childIndexParam: string | null,
  arraySize: number
): number {
  if (childIndexParam === null || childIndexParam.trim() === "") return 0

  const parsed = Number(childIndexParam)
  const isInRange = Number.isInteger(parsed) && parsed >= 0 && parsed < arraySize

  return isInRange ? parsed : 0
}

/**
 * HPC Job Logs viewer component.
 *
 * Displays log output for a specific Batch job in a terminal-style scrollable
 * container. Supports "follow" mode that polls for new log entries every 5
 * seconds. Auto-scrolls to bottom unless the user has scrolled up.
 *
 * Route params:
 *   - jobId: The AWS Batch job ID (from URL path)
 *
 * Query params (all optional):
 *   - jobName: Human-readable job name shown in the header
 *   - status: Job status used as a placeholder until the first status fetch
 *   - arraySize: Number of child tasks, which enables the child selector
 *   - childIndex: Child task to open initially, for links to a specific child
 */
export default function HpcJobLogsView() {
  const { jobId } = useParams<{ jobId: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { getAccessToken } = useAuth()

  const jobName = searchParams.get("jobName") || jobId || ""
  const statusParam = searchParams.get("status") as JobStatus | null

  /**
   * Array job detection from query params.
   * If arraySize > 0, this is an array job with multiple child tasks.
   * The arraySize parameter is passed from the jobs table when navigating to this page.
   */
  const arraySize = parseInt(searchParams.get("arraySize") || "0", 10)

  /** Whether this job is an array job (has child tasks). */
  const isArrayJob = arraySize > 0

  /** The child task to open on first render, from the childIndex query param. */
  const initialChildIndex = resolveInitialChildIndex(searchParams.get("childIndex"), arraySize)

  /**
   * The confirmed child index for array jobs.
   * This is the index used when fetching logs from the API.
   * Valid range: 0 to arraySize - 1.
   */
  const [childIndex, setChildIndex] = useState<number>(initialChildIndex)

  /**
   * The input field value for child index selection.
   * Stored as string to handle partial/invalid input during typing.
   * Synchronized with childIndex when user confirms selection.
   */
  const [childIndexInput, setChildIndexInput] = useState<string>(String(initialChildIndex))

  /**
   * Validation error message for the child index input.
   * Displayed below the input field when the value is invalid.
   * Empty string means no error (valid input).
   */
  const [childIndexError, setChildIndexError] = useState<string>("")

  /**
   * Reference to the child index input field for focus management.
   * Used to retain focus when Enter is pressed with invalid value.
   */
  const childIndexInputRef = useRef<HTMLInputElement>(null)

  /**
   * Validates the child index input value.
   * Checks that the value is a valid integer within the array job range.
   *
   * @param value - The input string value to validate.
   * @returns An error message string if invalid, or empty string if valid.
   */
  function validateChildIndex(value: string): string {
    // Empty input is invalid but we don't show an error message for it
    if (value.trim() === "") {
      return ""
    }

    // Check if it's a valid integer (no decimals, no non-numeric characters)
    const parsed = Number(value)
    if (!Number.isInteger(parsed)) {
      return "Index must be a whole number"
    }

    // Check range: 0 <= value < arraySize
    if (parsed < 0 || parsed >= arraySize) {
      return `Index must be between 0 and ${arraySize - 1}`
    }

    return ""
  }

  /**
   * Determines if the current child index input is valid for submission.
   * Used to enable/disable the "Go" button.
   *
   * @returns True if the input is a valid child index, false otherwise.
   */
  function isChildIndexInputValid(): boolean {
    const trimmed = childIndexInput.trim()
    if (trimmed === "") {
      return false
    }
    const parsed = Number(trimmed)
    return Number.isInteger(parsed) && parsed >= 0 && parsed < arraySize
  }

  // Log state
  const [logEntries, setLogEntries] = useState<LogEntry[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)

  // Job metadata state, fetched from the status endpoint for the details panel.
  const [jobDetails, setJobDetails] = useState<HpcJob | null>(null)
  const [detailsError, setDetailsError] = useState<string | null>(null)

  // Follow/tail state
  const [followLogs, setFollowLogs] = useState<boolean>(false)

  /**
   * The job ID used for all API calls on this page. Array job children are
   * addressed as 'parentJobId:childIndex'; normal jobs use the route ID as-is.
   */
  const effectiveJobId = isArrayJob ? `${jobId}:${childIndex}` : jobId

  /**
   * The job's current status. Prefers the freshly fetched value; the status
   * passed in the URL is only a placeholder until the first fetch resolves,
   * and can be stale for a job that has since progressed.
   */
  const jobStatus: JobStatus | null = jobDetails?.status ?? statusParam

  // Refs for tailing and auto-scroll
  const newestIngestionTimeRef = useRef<number | undefined>(undefined)
  const tailIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const logContainerRef = useRef<HTMLDivElement>(null)
  const autoScrollRef = useRef<boolean>(true)

  const isTerminal = jobStatus === JobStatus.SUCCEEDED || jobStatus === JobStatus.FAILED

  /**
   * Deduplicates incoming log entries against existing ones.
   * Uses timestamp + message as a composite key.
   *
   * @param existing - Currently displayed log entries.
   * @param incoming - Newly fetched log entries.
   * @returns Merged array with duplicates removed.
   */
  function deduplicateLogs(existing: LogEntry[], incoming: LogEntry[]): LogEntry[] {
    const seen = new Set(existing.map((e) => `${e.timestamp}:${e.message}`))
    const newEntries = incoming.filter((e) => !seen.has(`${e.timestamp}:${e.message}`))
    return [...existing, ...newEntries]
  }

  /**
   * Fetches the initial batch of log entries (most recent).
   */
  const fetchInitialLogs = useCallback(async () => {
    if (!effectiveJobId) return
    setIsLoading(true)
    setError(null)

    try {
      const token = await getAccessToken()
      const response = await getJobLogs({ jobId: effectiveJobId, limit: INITIAL_LIMIT }, token)
      setLogEntries(response.logEvents)
      if (response.newestLogIngestionTime) {
        newestIngestionTimeRef.current = response.newestLogIngestionTime
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load logs"
      setError(message)
    } finally {
      setIsLoading(false)
    }
  }, [effectiveJobId, getAccessToken])

  /**
   * Fetches newer log entries for tailing (entries since the last ingestion time).
   */
  const fetchNewerLogs = useCallback(async () => {
    if (!effectiveJobId) return

    try {
      const token = await getAccessToken()
      const response = await getJobLogs(
        {
          jobId: effectiveJobId,
          limit: 100,
          startTime: newestIngestionTimeRef.current !== undefined
            ? String(newestIngestionTimeRef.current)
            : undefined,
        },
        token
      )

      if (response.logEvents.length > 0) {
        setLogEntries((prev) => deduplicateLogs(prev, response.logEvents))
      }
      if (response.newestLogIngestionTime) {
        newestIngestionTimeRef.current = response.newestLogIngestionTime
      }
    } catch {
      // Silently handle tail errors — will retry on next interval
    }
  }, [effectiveJobId, getAccessToken])

  /**
   * Fetches the job's metadata from the status endpoint.
   *
   * Called on mount, on each tail poll while following, and on manual refresh,
   * so that status, end time and duration stay current as the job progresses.
   * Errors are surfaced inside the details panel and leave any previously
   * loaded metadata in place.
   */
  const fetchJobStatus = useCallback(async () => {
    if (!effectiveJobId) return

    try {
      const token = await getAccessToken()
      // The status endpoint always returns an array. This page addresses a
      // single job (or a single array child), so it yields one entry.
      const [status] = await getJobStatus(effectiveJobId, token)
      if (!status) {
        throw new Error(`No job found with ID ${effectiveJobId}`)
      }
      setJobDetails(transformJobData(status))
      setDetailsError(null)
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load job details"
      setDetailsError(message)
    }
  }, [effectiveJobId, getAccessToken])

  // Initial fetch on mount
  useEffect(() => {
    fetchInitialLogs()
  }, [fetchInitialLogs])

  // Fetch job metadata on mount, and again whenever the target job changes
  // (e.g. the user selects a different array job child).
  useEffect(() => {
    fetchJobStatus()
  }, [fetchJobStatus])

  // Auto-scroll when new logs arrive
  useEffect(() => {
    if (autoScrollRef.current && logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight
    }
  }, [logEntries])

  // Follow/tail interval. Each tick also refreshes the job metadata so the
  // details panel tracks the running job and the page notices it finishing.
  useEffect(() => {
    if (followLogs && !isTerminal) {
      tailIntervalRef.current = setInterval(() => {
        void fetchNewerLogs()
        void fetchJobStatus()
      }, TAIL_INTERVAL_MS)
    }
    return () => {
      if (tailIntervalRef.current) {
        clearInterval(tailIntervalRef.current)
        tailIntervalRef.current = null
      }
    }
  }, [followLogs, isTerminal, fetchNewerLogs, fetchJobStatus])

  // Stop following if job is terminal
  useEffect(() => {
    if (isTerminal && followLogs) {
      setFollowLogs(false)
    }
  }, [isTerminal, followLogs])

  /**
   * Tracks user scroll position to disable auto-scroll when scrolled up.
   */
  function handleScroll(): void {
    if (!logContainerRef.current) return
    const { scrollTop, scrollHeight, clientHeight } = logContainerRef.current
    autoScrollRef.current = scrollHeight - scrollTop - clientHeight < 50
  }

  /**
   * Handles changes to the child index input field.
   * Validates the input and updates the error state accordingly.
   *
   * @param event - The input change event.
   */
  function handleChildIndexInputChange(event: React.ChangeEvent<HTMLInputElement>): void {
    const value = event.target.value
    setChildIndexInput(value)

    // Validate and set error message (but don't show error for empty input)
    const error = validateChildIndex(value)
    setChildIndexError(error)
  }

  /**
   * Handles confirmation of the child index selection.
   * Validates the input, updates the confirmed childIndex state,
   * resets follow logs, clears existing logs, and triggers a new fetch.
   *
   * This function is called when the user clicks "Go" or presses Enter
   * with a valid child index value.
   */
  function handleGoToChildIndex(): void {
    // Validate input before proceeding
    if (!isChildIndexInputValid()) {
      // Focus the input field when validation fails
      childIndexInputRef.current?.focus()
      return
    }

    const newChildIndex = Number(childIndexInput.trim())

    // Update the confirmed child index (this triggers a new fetch via useEffect)
    setChildIndex(newChildIndex)

    // Reset follow logs when changing child index
    setFollowLogs(false)

    // Clear existing log entries to show loading state for new child
    setLogEntries([])

    // Drop the previous child's metadata so the details panel doesn't show
    // stale values while the new child's status is being fetched.
    setJobDetails(null)

    // Reset the newest ingestion time for the new child's logs
    newestIngestionTimeRef.current = undefined
  }

  /**
   * Handles keyboard events in the child index input field.
   * Triggers log loading when Enter is pressed with a valid value.
   * Retains focus on input when Enter is pressed with an invalid value.
   *
   * @param event - The keyboard event.
   */
  function handleChildIndexKeyDown(event: React.KeyboardEvent<HTMLInputElement>): void {
    if (event.key === "Enter") {
      event.preventDefault()

      if (isChildIndexInputValid()) {
        handleGoToChildIndex()
      } else {
        // Retain focus on input when value is invalid
        childIndexInputRef.current?.focus()
      }
    }
  }

  /**
   * Refreshes both the log output and the job metadata.
   * Used by the manual refresh button.
   */
  function handleRefresh(): void {
    void fetchInitialLogs()
    void fetchJobStatus()
  }

  /**
   * Builds the link to a workflow's detail page, carrying through the return
   * params this page was opened with so the workflow page's Back button lands
   * the user where they started.
   *
   * @param workflowId - The workflow ID to link to.
   * @returns A router path with the workflow ID and preserved return params.
   */
  function buildWorkflowHref(workflowId: string): string {
    const params = new URLSearchParams({ view: "jobs" })

    const returnTab = searchParams.get("tab")
    const returnRange = searchParams.get("range")
    if (returnTab) params.set("tab", returnTab)
    if (returnRange) params.set("range", returnRange)
    if (searchParams.get("from") === "admin") params.set("from", "admin")

    return `/computing/hpc/workflow/${encodeURIComponent(workflowId)}?${params.toString()}`
  }

  return (
    <>
    <title>HPC Job Logs - MAP Apps Platform</title>
    <div className="flex flex-1 flex-col min-h-0 p-6 gap-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back
            </Button>
          <h1 className="text-lg font-semibold truncate max-w-md" title={jobName}>
            {jobName}
          </h1>
        </div>

        {/* Child Index Selector - only shown for array jobs */}
        {isArrayJob && (
          <div className="flex flex-col">
            <div className="flex items-center gap-2">
              <label
                htmlFor="child-index-input"
                className="text-sm text-muted-foreground whitespace-nowrap"
              >
                Child Task (0 - {arraySize - 1}):
              </label>
              <Input
                id="child-index-input"
                ref={childIndexInputRef}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={childIndexInput}
                onChange={handleChildIndexInputChange}
                onKeyDown={handleChildIndexKeyDown}
                aria-label={`Child index, valid range 0 to ${arraySize - 1}`}
                aria-invalid={childIndexError !== ""}
                aria-describedby={childIndexError ? "child-index-error" : undefined}
                className="w-20"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={handleGoToChildIndex}
                disabled={!isChildIndexInputValid() || isLoading}
              >
                Go
              </Button>
            </div>
            {/* Validation error message */}
            {childIndexError && (
              <p
                id="child-index-error"
                className="text-xs text-destructive mt-1"
                role="alert"
              >
                {childIndexError}
              </p>
            )}
          </div>
        )}

        <div className="flex items-center gap-3">
          <label className={`flex items-center gap-2 text-sm cursor-pointer ${isTerminal ? "opacity-50" : ""}`}>
            <input
              type="checkbox"
              checked={followLogs}
              onChange={(e) => {
                setFollowLogs(e.target.checked)
                if (e.target.checked) autoScrollRef.current = true
              }}
              disabled={isTerminal}
              className="h-4 w-4"
            />
            Follow logs
          </label>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={isLoading}
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="rounded-md border border-destructive bg-destructive/10 p-4">
          <p className="text-sm text-destructive">{error}</p>
          <Button variant="outline" size="sm" onClick={() => fetchInitialLogs()} className="mt-2">
            Retry
          </Button>
        </div>
      )}

      {/* Job details, fetched from the status endpoint */}
      <div className="rounded-md border p-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3">
          <DetailField label="Name">{jobDetails?.jobName ?? jobName}</DetailField>

          <DetailField label="Job ID">
            <div className="flex items-center gap-1 min-w-0">
              <CopyButton text={effectiveJobId ?? ""} />
              <span className="font-mono text-xs truncate" title={effectiveJobId}>
                {effectiveJobId}
              </span>
            </div>
          </DetailField>

          <DetailField label="Workflow ID">
            {jobDetails?.workflowId ? (
              <div className="flex items-center gap-1 min-w-0">
                <CopyButton text={jobDetails.workflowId} />
                <Link
                  to={buildWorkflowHref(jobDetails.workflowId)}
                  className="font-mono text-xs truncate hover:underline"
                  title={jobDetails.workflowName ?? jobDetails.workflowId}
                >
                  {jobDetails.workflowId}
                </Link>
              </div>
            ) : (
              "-"
            )}
          </DetailField>

          <DetailField label="User">{jobDetails?.user ?? "-"}</DetailField>

          <DetailField label="Status">
            {jobStatus ? (
              <Badge variant={getStatusVariant(jobStatus)}>{jobStatus}</Badge>
            ) : (
              "-"
            )}
          </DetailField>

          <DetailField label="Start Time">
            {formatJobTimestamp(jobDetails?.startedAt)}
          </DetailField>

          <DetailField label="End Time">
            {formatJobTimestamp(jobDetails?.stoppedAt)}
          </DetailField>

          <DetailField label="Duration">
            {formatJobDuration(jobDetails?.startedAt, jobDetails?.stoppedAt)}
          </DetailField>

          {/* Status reasons are free text and can be long, so give them the
              full width of the panel rather than a single grid cell. */}
          <DetailField label="Status Reason" className="col-span-2 md:col-span-4">
            {jobDetails?.statusReason || "-"}
          </DetailField>
        </div>

        {detailsError && (
          <p className="text-xs text-destructive mt-3" role="alert">
            Failed to load job details: {detailsError}
          </p>
        )}
      </div>

      {/* Log output */}
      <h2>Job Log</h2>
      <div
        ref={logContainerRef}
        onScroll={handleScroll}
        role="log"
        aria-label="Job log output"
        aria-live={followLogs ? "polite" : "off"}
        className="flex-1 min-h-0 overflow-y-auto rounded-md bg-muted p-4 font-mono text-xs leading-relaxed"
      >
        {isLoading && logEntries.length === 0 ? (
            <LoadingSpinner loadingContent="Loading logs..." />
        ) : logEntries.length === 0 ? (
          <div className="text-muted-foreground">No log entries found.</div>
        ) : (
          logEntries.map((entry, index) => (
            <div key={`${entry.timestamp}-${index}`} className="flex gap-3 py-0.5">
              <span className="shrink-0 text-muted-foreground">
                {formatLogTimestamp(entry.timestamp)}
              </span>
              <span className="whitespace-pre-wrap break-all">{entry.message}</span>
            </div>
          ))
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>{logEntries.length} log entries</span>
        {followLogs && !isTerminal && (
          <span className="flex items-center gap-1">
            <span className="inline-block h-2 w-2 rounded-full bg-green-500 animate-pulse" />
            Tailing...
          </span>
        )}
      </div>
    </div>
    </>
  )
}


